import { ReplotChartNames } from '@revelio/replots';

/**
 * Chart types PowerPoint has no native form for. Forcing these through
 * `addChart` silently degrades them: a Sankey came out as a bar chart of
 * Count/Share pairs across two categories, with the whole flow structure gone.
 * These are captured from the rendered DOM and placed as pictures instead.
 */
const IMAGE_ONLY_CHART_TYPES: ReadonlySet<string> = new Set([
  ReplotChartNames.Sankey,
  ReplotChartNames.Chord,
  ReplotChartNames.TreeMap,
  ReplotChartNames.IcicleChart,
]);

export const isImageOnlyChartType = (chartType?: string | null): boolean =>
  !!chartType && IMAGE_ONLY_CHART_TYPES.has(chartType);

/** Flow charts whose captured picture gets a "largest flows" figures panel. */
const FLOW_FIGURE_CHART_TYPES: ReadonlySet<string> = new Set([
  ReplotChartNames.Sankey,
  ReplotChartNames.Chord,
]);

export const isFlowFigureChartType = (chartType?: string | null): boolean =>
  !!chartType && FLOW_FIGURE_CHART_TYPES.has(chartType);

export type CapturedChartImage = {
  dataUrl: string;
  /** On-screen pixel size, used only to preserve aspect ratio on the slide. */
  width: number;
  height: number;
};

const CAPTURE_SCALE = 2;

/** Icon-sized svgs (menu glyphs, legend chips) are never the chart. */
export const MIN_CHART_SVG_AREA_PX = 10_000;

/**
 * The chart card wraps its plot in incidental UI (controls like "Show 10",
 * spacer flex containers), so capturing the card yields mostly dead space.
 * Replots charts are SVG — the largest <svg> in the card IS the chart, and
 * its bounding box is exactly the visual worth exporting.
 */
const findChartSvg = (el: HTMLElement): SVGSVGElement | null => {
  let best: SVGSVGElement | null = null;
  let bestArea = MIN_CHART_SVG_AREA_PX;
  for (const svg of Array.from(el.querySelectorAll('svg'))) {
    const { width, height } = svg.getBoundingClientRect();
    const area = width * height;
    // Strict > so an equal-sized svg nested INSIDE the chart svg (document
    // order puts the parent first) can't shadow its parent.
    if (area > bestArea) {
      best = svg;
      bestArea = area;
    }
  }
  return best;
};

/**
 * Style properties that determine how static SVG chart content paints.
 * Replots styles parts of its charts through CSS-module classes and `--ui-*`
 * tokens (e.g. the sankey's `.path` / `.label`), and an SVG drawn as an image
 * can't see document stylesheets — so the computed values are inlined onto the
 * serialized clone, which also bakes in the active theme.
 */
const INLINED_STYLE_PROPS = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'opacity',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'letter-spacing',
  'text-anchor',
  'dominant-baseline',
  'paint-order',
  'visibility',
  'display',
] as const;

export const inlineComputedStyles = (source: Element, target: Element) => {
  const computed = (
    source.ownerDocument.defaultView ?? window
  ).getComputedStyle(source);
  const style = (target as SVGElement | HTMLElement).style;
  for (const prop of INLINED_STYLE_PROPS) {
    style.setProperty(prop, computed.getPropertyValue(prop));
  }
  const sourceChildren = source.children;
  const targetChildren = target.children;
  for (let i = 0; i < sourceChildren.length; i += 1) {
    const sourceChild = sourceChildren[i];
    const targetChild = targetChildren[i];
    if (sourceChild && targetChild) {
      inlineComputedStyles(sourceChild, targetChild);
    }
  }
};

export const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/**
 * Rasterise an SVG chart via the browser's own renderer: serialize the styled
 * clone to a data URL, draw it onto a canvas, export as PNG. Unlike
 * html2canvas — which repaints SVG itself and mangled the sankey's ribbons
 * into solid blocks — this draws with the same engine that rendered the chart
 * on screen.
 */
const rasterizeSvg = async (
  svg: SVGSVGElement,
  backgroundColor: string
): Promise<CapturedChartImage | null> => {
  const { width, height } = svg.getBoundingClientRect();
  if (width <= 0 || height <= 0) return null;

  const clone = svg.cloneNode(true) as SVGSVGElement;
  inlineComputedStyles(svg, clone);
  // Replots svgs size themselves as width/height="100%" — the clone renders
  // outside that layout context, so pin the on-screen pixel size.
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));

  const markup = new XMLSerializer().serializeToString(clone);
  const image = await loadImage(
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`
  );

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * CAPTURE_SCALE);
  canvas.height = Math.round(height * CAPTURE_SCALE);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.scale(CAPTURE_SCALE, CAPTURE_SCALE);
  ctx.drawImage(image, 0, 0, width, height);

  return { dataUrl: canvas.toDataURL('image/png'), width, height };
};

/** Slides are white; a transparent capture must not composite to black. */
const resolveBackground = (el: HTMLElement): string => {
  const computedBg = getComputedStyle(el).backgroundColor;
  return !computedBg || computedBg === 'rgba(0, 0, 0, 0)'
    ? '#FFFFFF'
    : computedBg;
};

/**
 * Capture a chart card's visual from the live DOM. Prefers native SVG
 * rasterization of the chart's own <svg>; falls back to an html2canvas
 * screenshot of the whole element (imported on demand — it's heavy) for
 * charts that aren't a single svg.
 *
 * Returns `null` when the element isn't laid out (unmounted, zero-size, or an
 * export built from data alone) so callers can fall back instead of throwing.
 */
export const captureChartImage = async (
  el: HTMLElement | null | undefined
): Promise<CapturedChartImage | null> => {
  if (!el) return null;

  const { width, height } = el.getBoundingClientRect();
  if (width <= 0 || height <= 0) return null;

  const backgroundColor = resolveBackground(el);

  const svg = findChartSvg(el);
  if (svg) {
    try {
      const image = await rasterizeSvg(svg, backgroundColor);
      if (image) return image;
    } catch {
      // e.g. a tainted canvas from an embedded <image> — try html2canvas.
    }
  }

  try {
    const { default: html2canvas } = await import('html2canvas-pro');
    const canvas = await html2canvas(el, {
      backgroundColor,
      scale: CAPTURE_SCALE,
    });
    return { dataUrl: canvas.toDataURL('image/png'), width, height };
  } catch {
    // A failed capture must not abort the whole deck.
    return null;
  }
};

/**
 * Fit a captured image inside a slide box without distorting it, centred on
 * both axes. pptxgenjs stretches to the given w/h, so the aspect ratio has to
 * be applied here rather than left to PowerPoint.
 */
export const fitImageToBox = (
  image: CapturedChartImage,
  box: { x: number; y: number; w: number; h: number }
): { x: number; y: number; w: number; h: number } => {
  const scale = Math.min(box.w / image.width, box.h / image.height);
  const w = image.width * scale;
  const h = image.height * scale;

  return {
    x: box.x + (box.w - w) / 2,
    y: box.y + (box.h - h) / 2,
    w,
    h,
  };
};
